import { createApiClient } from '@neon-one/auctions-types';
import type { AxiosInstance } from 'axios';

import { env } from '@/shell/env';
import { getOrgSlug } from '@/shell/org';
import { attachCorrelationId, CORRELATION_ID_HEADER, logger } from '@/shell/observability';
import { useSession } from '@/shell/stores/session';

let _client: AxiosInstance | null = null;

export function httpClient(): AxiosInstance {
  if (_client) return _client;
  _client = createApiClient({
    baseUrl: env.apiBaseUrl,
    getJwt: () => useSession().getJwt(),
  });
  _client.interceptors.request.use(config => {
    const slug = getOrgSlug();
    if (slug) config.headers['X-Org-Slug'] = slug;
    // Stamp a per-request correlation id so the backend (GT1-391) threads it
    // through its logs and echoes it back. Log method + path only — never
    // bodies, headers, or query values.
    const correlationId = attachCorrelationId(config);
    // Strip any inline query string / fragment so sensitive URL values
    // (tokens, emails) never reach the log. Log method + path only.
    const path = (config.url ?? '').split(/[?#]/, 1)[0];
    logger.debug('http_request', {
      correlationId,
      method: (config.method ?? 'get').toUpperCase(),
      path,
    });
    return config;
  });
  _client.interceptors.response.use(
    (resp) => {
      // Closes the loop on the http_request debug log above — without this,
      // the request side of a correlation id is logged but never its
      // outcome, so a request that hung or a slow endpoint can't be spotted
      // from client logs alone.
      logger.debug('http_response', {
        correlationId: resp.config.headers?.get?.(CORRELATION_ID_HEADER) as string | undefined,
        status: resp.status,
        path: (resp.config.url ?? '').split(/[?#]/, 1)[0],
      });
      return resp;
    },
    (err) => {
      logger.debug('http_response', {
        correlationId: err?.config?.headers?.get?.(CORRELATION_ID_HEADER) as string | undefined,
        status: err?.response?.status ?? err?.status,
        path: (err?.config?.url ?? '').split(/[?#]/, 1)[0],
      });
      if (err?.response?.status === 401) {
        // Don't clear a mock session — prototype-issued JWTs always
        // 401 against the real backend, but the user shouldn't get
        // bounced out of the demo because of it.
        const jwt = useSession().jwt;
        if (!jwt || !jwt.startsWith("mock-jwt-")) {
          useSession().clear();
        }
      }
      throw err;
    },
  );
  return _client;
}
